interface InvoiceTemplateProps {
  balance: string;
  balanceColor: string;
  invoiceDate: string;
  invoiceUrl?: string;
  logo: string;
  numberOfItems: string;
  organizationAddress1: string;
  organizationAddress2?: string;
  organizationCity: string;
  organizationEmail: string;
  organizationPhone: string;
  organizationState: string;
  organizationTitle: string;
  organizationUrl: string;
  organizationZip: string;
  totalBillable: string;
  totalCharges: string;
  totalCoupons: string;
  totalCredits: string;
  totalDiscounts: string;
  totalPaid: string;
  totalRefunds: string;
  totalTaxes?: string;
  userEmail: string;
  userName: string;
}

export function InvoiceTemplate(props: InvoiceTemplateProps) {
  return (
    <div className="container">
      <table cellPadding="0" cellSpacing="0">
        <tbody>
          {props.logo && (
            <tr>
              <td>
                <img
                  className="logo"
                  src={props.logo}
                  alt={props.organizationTitle}
                  width="130"
                />
              </td>
            </tr>
          )}

          <tr>
            <td className="header">
              <table>
                <tbody>
                  <tr>
                    <td>
                      <span className="is-empty mb0">
                        <strong>Account</strong> • {props.userName}
                      </span>
                    </td>
                    <td className="is-empty text-right">
                      Invoice Date • {props.invoiceDate}
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>

          <tr className="totals">
            <td>
              <table className="totals__data">
                <tbody>
                  <tr>
                    <td>Invoice Total ({props.numberOfItems})</td>
                    <td className="text-right">${props.totalBillable}</td>
                  </tr>

                  <tr>
                    <td>Balance Due</td>
                    <td
                      className="text-right"
                      style={{ color: props.balanceColor }}
                    >
                      <strong>${props.balance}</strong>
                    </td>
                  </tr>

                  {props.totalRefunds && (
                    <tr>
                      <td className="meta is-empty">Refunds Issued</td>
                      <td className="meta is-empty text-right">
                        <strong>${props.totalRefunds}</strong>
                      </td>
                    </tr>
                  )}

                  <tr>
                    <td colSpan={2}>
                      <p className="mb0">
                        <strong>Invoice Totals</strong>
                      </p>
                    </td>
                  </tr>

                  <tr>
                    <td>Total Charges ({props.numberOfItems})</td>
                    <td className="text-right">${props.totalBillable}</td>
                  </tr>

                  {props.totalDiscounts && (
                    <tr>
                      <td>Total Discounts</td>
                      <td className="text-green text-right">
                        -${props.totalDiscounts}
                      </td>
                    </tr>
                  )}

                  {props.totalCoupons && (
                    <tr>
                      <td>Total Coupons</td>
                      <td className="text-green text-right">
                        -${props.totalCoupons}
                      </td>
                    </tr>
                  )}

                  {props.totalCredits && (
                    <tr>
                      <td>Total Credits</td>
                      <td className="text-green text-right">
                        -${props.totalCredits}
                      </td>
                    </tr>
                  )}

                  {props.totalTaxes && (
                    <tr>
                      <td className="text-right">Sales Tax</td>
                      <td className="text-right">${props.totalTaxes}</td>
                    </tr>
                  )}

                  <tr>
                    <td colSpan={2}>
                      <hr />
                    </td>
                  </tr>

                  <tr>
                    <td>Subtotal</td>
                    <td className="text-right">${props.totalCharges}</td>
                  </tr>

                  {props.totalPaid && (
                    <tr>
                      <td>Payment Received</td>
                      <td className="text-green text-right">
                        -${props.totalPaid}
                      </td>
                    </tr>
                  )}

                  <tr>
                    <td>
                      <strong>Balance Due</strong>
                    </td>
                    <td
                      className="text-right"
                      style={{ color: props.balanceColor }}
                    >
                      <strong>${props.balance}</strong>
                    </td>
                  </tr>

                  <tr>
                    <td colSpan={2}>
                      <p className="meta is-empty">
                        For itemized invoices and additional details, sign in to
                        your account at{" "}
                        <a href={props.invoiceUrl}>{props.organizationUrl}</a>.
                      </p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>

          <tr className="organization">
            <td colSpan={2} className="organization__data">
              <table>
                <tbody>
                  <tr>
                    <td>
                      <div className="meta is-empty text-center">
                        <strong>{props.organizationTitle}</strong>

                        {props.organizationAddress1 && (
                          <div>{props.organizationAddress1}</div>
                        )}

                        {props.organizationAddress2 && (
                          <div>{props.organizationAddress2}</div>
                        )}

                        {props.organizationCity && (
                          <div>
                            {props.organizationCity}, {props.organizationState}{" "}
                            {props.organizationZip}
                          </div>
                        )}

                        <div>
                          {props.organizationPhone}
                          {props.organizationPhone &&
                            props.organizationEmail &&
                            ` • `}
                          {props.organizationEmail && (
                            <>
                              <a
                                className="is-empty"
                                href={`mailto:${props.organizationEmail}`}
                              >
                                {props.organizationEmail}
                              </a>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export const invoiceFoot = `
</body>
</html>
`;

export const invoiceHead = `
<!doctype html>
<html>

<head>
  <meta charset="utf-8">
  <title>Invoice</title>
  <link rel="stylesheet" href="https://use.typekit.net/dlm1ocu.css">
  <style>
    html,
    body {
      background: white;
      font-family: "Soleil", sans-serif;
      font-size: 13px;
      line-height: 1.5;
      color: black;
    }

    h1,
    h2,
    h3,
    h4 {
      margin-top: 0;
    }

    .mb0 {
      margin-bottom: 0;
    }

    .is-empty {
      color: rgb(119, 119, 119);
    }

    .container {
      max-width: 800px;
      margin: auto;
      padding: 10px;
    }

    table {
      width: 100%;
      line-height: inherit;
      text-align: left;
      border-collapse: collapse;
    }

    td {
      padding: 5px;
      vertical-align: text-bottom;
    }

    .text-right {
      text-align: right;
    }

    .text-center {
      text-align: center;
    }

    .text-green {
      color: #2ed6a1;
    }

    .text-red {
      color: #d0021b;
    }

    .header {
      padding-bottom: 1rem;
      border-bottom: 1px solid #ccc;
    }

    .user {
      border-bottom: 1px solid black;
      font-weight: bold;
      padding-left: 10px;
    }

    tr.item > td {
      padding-left: 2rem;
      border-top: 1px solid rgb(204, 204, 204);
    }

    .meta {
      font-size: .85rem;
    }

    .totals > td {
      padding-top: 2rem;
    }

    .totals__data {
      width: 60%;
      margin: auto;
    }

    .totals__data td {
      padding-top: 0;
    }

    .organization__data {
      padding-top: 5rem;
    }

    hr {
      border: none;
      border-top: 1px solid rgb(204, 204, 204);
      margin: .5rem 0;
    }

    .logo {
      padding: 0 5px;
    }

    a {
      text-decoration: none;
    }
  </style>
</head>
`;
